import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, FilterIcon, EyeIcon, EditIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { DatePicker } from '../../../components/ui/DatePicker';
import { VoucherStatusBadge } from '../../../components/voucher/VoucherStatusBadge';
import { Voucher, VoucherFilters, VoucherType, VoucherStatus } from '../../../types/voucher.types';
import { voucherService } from '../../../services/voucher.service';
export function JournalList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<VoucherFilters>({
    voucherType: VoucherType.JOURNAL
  });
  useEffect(() => {
    loadVouchers();
  }, [filters]);
  const loadVouchers = async () => {
    setLoading(true);
    try {
      const data = await voucherService.getVouchers(filters);
      const filtered = data.filter(v => v.voucherType === VoucherType.JOURNAL);
      setVouchers(filtered);
    } catch (error) {
      console.error('Failed to load vouchers:', error);
    } finally {
      setLoading(false);
    }
  };
  const statusOptions = Object.values(VoucherStatus).map(status => ({
    value: status,
    label: status
  }));
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1723">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1724">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1725">
          <div data-id="element-1726">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1727">
              Journal Voucher List
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1728">
              Manage journal entries and adjustments
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1729">
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} data-id="element-1730">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-1731" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            <Button onClick={() => navigate('/voucher/journal/create')} data-id="element-1732">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1733" />
              Create New Journal Voucher
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && <div className="bg-white p-4 rounded-lg border space-y-4" data-id="element-1734">
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-1735">
              Search Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1736">
              <Input label="Voucher Number" placeholder="Search by number..." value={filters.voucherNo || ''} onChange={e => setFilters({
            ...filters,
            voucherNo: e.target.value
          })} data-id="element-1737" />

              <Select label="Status" options={statusOptions} value={filters.status || ''} onChange={e => setFilters({
            ...filters,
            status: e.target.value as VoucherStatus
          })} data-id="element-1738" />

              <DatePicker label="Date From" value={filters.dateFrom || ''} onChange={date => setFilters({
            ...filters,
            dateFrom: date
          })} data-id="element-1739" />

              <DatePicker label="Date To" value={filters.dateTo || ''} onChange={date => setFilters({
            ...filters,
            dateTo: date
          })} data-id="element-1740" />
            </div>
            <div className="flex justify-end" data-id="element-1741">
              <Button variant="secondary" size="sm" onClick={() => setFilters({
            voucherType: VoucherType.JOURNAL
          })} data-id="element-1742">
                Clear Filters
              </Button>
            </div>
          </div>}

        {/* Voucher Table */}
        <div className="bg-white rounded-lg border overflow-hidden" data-id="element-1743">
          {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1744">
              Loading vouchers...
            </div> : <div className="overflow-x-auto" data-id="element-1745">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1746">
                <thead className="bg-gray-50" data-id="element-1747">
                  <tr data-id="element-1748">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1749">
                      Voucher No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1750">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1751">
                      Description
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1752">
                      Debit Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1753">
                      Credit Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1754">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1755">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1756">
                  {vouchers.length === 0 ? <tr data-id="element-1757">
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1758">
                        No journal vouchers found. Click "Create New" to add
                        one.
                      </td>
                    </tr> : vouchers.map(voucher => <tr key={voucher.id} className="hover:bg-gray-50" data-id="element-1759">
                        <td className="px-6 py-4" data-id="element-1760">
                          <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1761">
                            {voucher.voucherNo}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1762">
                          {new Date(voucher.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1763">
                          {voucher.description || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-1764">
                          ${voucher.totalDebit.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium" data-id="element-1765">
                          ${voucher.totalCredit.toLocaleString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-1766">
                          <VoucherStatusBadge status={voucher.status} data-id="element-1767" />
                        </td>
                        <td className="px-6 py-4" data-id="element-1768">
                          <div className="flex items-center justify-center gap-2" data-id="element-1769">
                            <button onClick={() => navigate(`/voucher/${voucher.id}`)} className="text-gray-600 hover:text-gray-900" title="View Details" data-id="element-1770">
                              <EyeIcon className="w-4 h-4" data-id="element-1771" />
                            </button>
                            {voucher.isEditable && <button onClick={() => navigate(`/voucher/${voucher.id}/edit`)} className="text-gray-600 hover:text-gray-900" title="Edit" data-id="element-1772">
                                <EditIcon className="w-4 h-4" data-id="element-1773" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}